import { useCallback } from "react"
import { useSelector } from "react-redux"

// Droits d'accès front. La table en dur a DISPARU : la matrice vit côté serveur
// (`backend/svp/access.py`) et arrive par `GET api/access` au boot de l'app
// (slice `accessInfo`). Conséquences :
//
// - une seule source de vérité, plus de miroir front à maintenir ;
// - le bypass boss est appliqué PAR LE SERVEUR (boss reçoit toutes les clés) ;
// - `accessLoaded` dit si la map est arrivée — les gardes de route doivent
//   ATTENDRE et non rediriger tant qu'elle ne l'est pas (sinon chaque F5
//   éjecte l'utilisateur vers /home).
//
// ⚠️ Gating cosmétique : masque des entrées/routes. L'autorisation réelle reste
// enforcée côté backend (permissions DRF `HasModule`), pas ici.
//
// Exception assumée : `notifications` ne vient PAS de la matrice de modules. Son
// éligibilité a sa propre logique (pas de bypass boss — recevoir un flux est un
// choix, pas un privilège) et elle est éditable en base par le boss
// (Configuration → Notifications). Le serveur la résout et la renvoie dans le
// même payload : on a la page /notifications dès qu'on reçoit une catégorie.
export const useRights = () => {
	const { modules, notifCategories, loaded } = useSelector(
		(state) => state.accessInfo,
	)

	const hasRight = useCallback(
		(route) => {
			if (route === "notifications") return notifCategories.length > 0
			// Permissif par défaut : une clé hors matrice reste visible
			// (comportement historique — /home, /clients, /parc, /agenda…).
			if (!CLES_GATEES.includes(route)) return true
			// Matrice pas encore reçue : on ne tranche pas. ProtectedRoute
			// affiche un loader (`accessLoaded`) au lieu de rediriger.
			if (!loaded) return false
			return modules.includes(route)
		},
		[modules, notifCategories, loaded],
	)

	return { hasRight, accessLoaded: loaded }
}

// Clés gouvernées par la matrice serveur. Cette liste ne dit PAS qui a le droit
// (c'est le serveur), seulement quelles clés sont gatées — sans elle on ne peut
// pas distinguer « pas le droit » de « route non gatée » quand la map est vide.
// Doit rester alignée sur `MODULES` de backend/svp/access.py.
// ⚠️ DOIT contenir TOUTES les clés de `MODULES` + `MODULES_DERIVES` du backend.
// Une clé oubliée ici est traitée comme « non gatée » → visible par tous, en
// silence. Un test d'alignement mécanique existe (cf. tasks.md).
const CLES_GATEES = [
	// Pages
	"stats",
	"configuration",
	"erreurs",
	"patchnotes",
	"encaissements",
	// Revue interne 19.10 (R5) : dater une transaction (dérivé).
	"dater_transactions",
	"facturation",
	"agenda",
	"clients",
	"dep_dispatch",
	"dep_etat",
	"atelier",
	"atelier_modeles",
	"inventaire",
	"inventaire_virtuel",
	"inventaire_utilisations",
	"inventaire_page",
	"entretiens",
	"taches",
	"location",
	// Onglets des stats
	"stats_financier",
	"stats_interventions",
	"stats_mecanique",
	"stats_location",
	// Onglets de la configuration
	"config_identite",
	"config_vehicules",
	"config_entites",
	"config_rh",
	"config_rh_groupes",
	"config_paiements",
	"config_adresses",
	"config_depannage",
	"tarification_entreprise",
	"config_marques",
	"config_taches",
	"config_emails",
	"config_notifications",
	// Droits « champ » (cases dans des fiches, pas des pages)
	"client_sur_facture",
	"client_mauvais_payeur",
	"dep_case_boss",
	"atelier_marge",
	"atelier_cloture",
	"facturer_fiche",
	"atelier_cloture_entretien",
	"vehicule_fiche_entretien",
	"atelier_suppression",
]
